import { capitalize } from '@utilities/theme';

if(!customElements.get('truefit-widget')) {
  /**
   * TrueFit Widget Custom Element
   *
   * Integrates with TrueFit's sizing recommendation system to provide
   * personalized size suggestions for products. Listens for product
   * variant changes and automatically selects recommended sizes.
   *
   * @class truefitWidget
   * @extends HTMLElement
   */
  class truefitWidget extends HTMLElement {
    /**
     * Creates an instance of truefitWidget
     *
     * @constructor
     */
    constructor() {
      super();

      /** @type {HTMLElement|null} Reference to the closest product overview element */
      this.productOverview = null;

      /** @type {HTMLElement|null} Reference to the TrueFit widget div element */
      this.tfcWDiv = null;

      /** @type {Object} Stores the current TrueFit recommendation data */
      this.recommendation = {};

      /** @type {boolean} Tracks if auto-select has already been initialized */
      this.autoSelectInitialized = false;

      /** @type {boolean} Tracks if the initial auto-select should be processed */
      this.shouldProcessAutoSelect = true;
    }

    /**
     * Checks if debug mode is enabled for this widget instance
     *
     * Debug mode is enabled by adding the 'debug' class to the widget element.
     * When enabled, additional console logging will be output for debugging purposes.
     *
     * @returns {boolean} True if debug mode is enabled, false otherwise
     * @memberof truefitWidget
     */
    allowDebug = () => {
      return this.classList.contains('debug');
    }

    /**
     * Called when the element is connected to the DOM
     *
     * Initializes the widget by finding required DOM elements,
     * setting up event listeners for product variant changes,
     * and initializing the TrueFit auto-select functionality.
     *
     * @memberof truefitWidget
     */
    connectedCallback() {
      this.productOverview = this.closest('.product-overview');
      this.tfcWDiv = this.querySelector('.tfc-fitrec-product');
      this.recommendation = {};

      if (this.allowDebug()) {
        console.log('[TrueFit Widget] Connected to DOM', {
          productOverview: !!this.productOverview,
          tfcWDiv: !!this.tfcWDiv
        });
      }

      this.initAutoSelect();
      this.initEventListeners();
    }

    /**
     * Initializes event listeners for product variant changes
     *
     * Sets up listeners for swatch changes and other product events that
     * require updating the TrueFit widget data.
     *
     * @memberof truefitWidget
     */
    initEventListeners = () => {
      // Listen for product variant/swatch changes to update TrueFit widget data
      document.addEventListener('product-overview:swatch-change', (event) => {
        if(event.target.id !== this.productOverview?.id) return;

        const swatch = event.detail.element;
        const swatchColor = capitalize(swatch.dataset.jsName);
        let swatchChild = swatch.dataset.jsProductId;

        if(swatch.dataset.jsChildProductId){
          swatchChild = swatch.dataset.jsChildProductId;
        }

        if (this.allowDebug()) {
          console.log('[TrueFit Widget] Swatch change detected', {
            swatchColor,
            swatchChild,
            swatch: swatch.dataset
          });
        }

        this.updateTrueFitProduct(swatchChild, swatchColor);
      });
    }

    /**
     * Updates TrueFit product data via the API
     *
     * Safely calls the TrueFit API to update product information when
     * variants change. This only updates the product data and does NOT
     * trigger auto-select functionality - that should only happen on initial load.
     *
     * @param {string} styleId - The product style ID
     * @param {string} colorId - The color ID for the variant
     * @memberof truefitWidget
     */
    updateTrueFitProduct = (styleId, colorId) => {
      if (!styleId) {
        if (this.allowDebug()) {
          console.warn('[TrueFit Widget] No style ID provided for update');
        }
        return;
      }

      try {
        // Check if tfcapi is available
        if (typeof window.tfcapi !== 'function') {
          if (this.allowDebug()) {
            console.warn('[TrueFit Widget] tfcapi not available');
          }
          return;
        }

        const updateData = {
          products: {
            [styleId]: {
              colorId: colorId
            }
          }
        };

        if (this.allowDebug()) {
          console.log('[TrueFit Widget] Updating TrueFit product data (variant change, no auto-select)', updateData);
        }

        // This update call should only update the widget's product context
        // It should NOT trigger the autoSizeSelect event again
        window.tfcapi('update', 'tfc-fitrec-product', updateData);

      } catch (error) {
        console.error('[TrueFit Widget] Error updating TrueFit product:', error);
      }
    }

    /**
     * Maps common size variations to alternative representations
     *
     * TrueFit may return sizes in different formats than what's available
     * in the product variants. This method provides alternative size mappings
     * (e.g., "2xl" <-> "xxl", "3xl" <-> "xxxl").
     *
     * @param {string} originalSize - The original size to find an alternative for
     * @returns {string|null} The alternative size representation, or null if no mapping exists
     * @memberof truefitWidget
     */
    getAlternativeSize = (originalSize) => {
      if(!originalSize) return null;
      let alternativeSize = null;
      const translationMap = [
        { original: "2xl", alternative: "xxl" },
        { original: "xxl", alternative: "2xl" },
        { original: "3xl", alternative: "xxxl" },
        { original: "xxxl", alternative: "3xl" }
      ];

      const mapEntry = translationMap.find(entry => entry.original.toLowerCase() === originalSize.toLowerCase());
      if(mapEntry){
        alternativeSize = mapEntry.alternative;
      }

      return alternativeSize;
    }

    /**
     * Initializes the TrueFit auto-select functionality
     *
     * Sets up a callback with the TrueFit API to automatically receive
     * size recommendations when they become available. This should only
     * run once on initial component load, not on subsequent variant changes.
     *
     * @memberof truefitWidget
     */
    initAutoSelect = () => {
      // Prevent multiple initializations
      if (this.autoSelectInitialized) {
        if (this.allowDebug()) {
          console.log('[TrueFit Widget] AutoSelect already initialized, skipping');
        }
        return;
      }

      try {
        // Check if tfcapi is available
        if (typeof window.tfcapi !== 'function') {
          if (this.allowDebug()) {
            console.warn('[TrueFit Widget] tfcapi not available for autoSelect initialization');
          }
          return;
        }

        if (this.allowDebug()) {
          console.log('[TrueFit Widget] Initializing autoSizeSelect event listener (first time only)');
        }

        window.tfcapi('event', 'tfc-fitrec-product', 'autoSizeSelect', (event) => {
          if (this.allowDebug()) {
            console.log('[TrueFit Widget] AutoSizeSelect event received', event, { shouldProcess: this.shouldProcessAutoSelect });
          }

          const { styleId, size } = event;

          // Validate the event data
          if (!size) {
            if (this.allowDebug()) {
              console.warn('[TrueFit Widget] AutoSizeSelect event missing size data', event);
            }
            return;
          }

          // Always store the recommendation for potential future use
          this.recommendation = {
            styleId,
            size,
            timestamp: Date.now()
          };

          // Only trigger size selection if this is the initial auto-select
          // Skip auto-select events triggered by variant updates
          if (!this.shouldProcessAutoSelect) {
            if (this.allowDebug()) {
              console.log('[TrueFit Widget] Ignoring auto-select event (triggered by variant change)');
            }
            return;
          }

          if (this.allowDebug()) {
            console.log('[TrueFit Widget] Processing initial auto-size selection');
          }

          // After processing the first auto-select, disable further processing
          this.shouldProcessAutoSelect = false;

          this.handleSelectTrueFitSize(size);
        });

        // Mark as initialized to prevent duplicate event listeners
        this.autoSelectInitialized = true;

      } catch (error) {
        console.error('[TrueFit Widget] Error initializing TrueFit autoSelect:', error);
      }
    }

    /**
     * Processes TrueFit size recommendation and triggers size selection
     *
     * Takes the size recommendation, finds alternative size mappings if available,
     * and instructs the product overview component to select the recommended size variant.
     *
     * @param {string} size - The recommended size from TrueFit
     * @memberof truefitWidget
     */
    handleSelectTrueFitSize = (size) => {
      if (!size) {
        if (this.allowDebug()) {
          console.warn('[TrueFit Widget] No size provided for selection');
        }
        return;
      }

      if (this.allowDebug()) {
        console.log('[TrueFit Widget] Processing size recommendation', { size });
      }

      const suggestedSize = size;
      const alternativeSize = this.getAlternativeSize(suggestedSize);
      const suggestedSizes = {
        suggested: suggestedSize
      };

      if (alternativeSize) {
        suggestedSizes.alternative = alternativeSize;
        if (this.allowDebug()) {
          console.log('[TrueFit Widget] Found alternative size mapping', {
            original: suggestedSize,
            alternative: alternativeSize
          });
        }
      }

      if (this.productOverview && typeof this.productOverview.selectTrueFitSize === 'function') {
        if (this.allowDebug()) {
          console.log('[TrueFit Widget] Calling productOverview.selectTrueFitSize', suggestedSizes);
        }

        try {
          this.productOverview.selectTrueFitSize(suggestedSizes);
        } catch (error) {
          console.error('[TrueFit Widget] Error calling selectTrueFitSize:', error);
        }
      } else {
        const errorMsg = this.productOverview
          ? 'Product overview found but selectTrueFitSize method not available'
          : 'No product overview element found';

        if (this.allowDebug()) {
          console.warn('[TrueFit Widget]', errorMsg, {
            productOverview: this.productOverview,
            hasMethod: this.productOverview ? typeof this.productOverview.selectTrueFitSize : 'N/A'
          });
        }
      }
    }

    /**
     * Returns the current TrueFit recommendation data
     *
     * Provides access to the stored recommendation object that contains
     * size suggestions and other TrueFit data for the current product.
     *
     * @returns {Object} The current recommendation data from TrueFit
     * @memberof truefitWidget
     */
    getRecommendation = () => {
      if (this.allowDebug()) {
        console.log('[TrueFit Widget] Getting recommendation data', this.recommendation);
      }
      return this.recommendation;
    }

    /**
     * Checks if the TrueFit API is available and ready to use
     *
     * @returns {boolean} True if tfcapi is available, false otherwise
     * @memberof truefitWidget
     */
    isTrueFitApiAvailable = () => {
      const available = typeof window.tfcapi === 'function';
      if (this.allowDebug()) {
        console.log('[TrueFit Widget] API availability check', { available });
      }
      return available;
    }

    /**
     * Refreshes the TrueFit widget by reinitializing the auto-select functionality
     *
     * This can be useful when the page content changes dynamically or when
     * the TrueFit API becomes available after initial page load. Note that this
     * will reset the auto-select initialization and allow it to run again.
     *
     * @memberof truefitWidget
     */
    /**
     * Enables auto-select processing for the next recommendation
     *
     * This can be useful if you want to allow TrueFit to auto-select
     * a size again after the initial load (e.g., after a significant
     * product or user context change).
     *
     * @memberof truefitWidget
     */
    enableAutoSelect = () => {
      if (this.allowDebug()) {
        console.log('[TrueFit Widget] Re-enabling auto-select processing');
      }
      this.shouldProcessAutoSelect = true;
    }

    refresh = () => {
      if (this.allowDebug()) {
        console.log('[TrueFit Widget] Refreshing widget');
      }

      // Clear existing recommendation data
      this.recommendation = {};

      // Reset initialization flags to allow auto-select to run again
      this.autoSelectInitialized = false;
      this.shouldProcessAutoSelect = true;

      // Reinitialize auto-select if API is available
      if (this.isTrueFitApiAvailable()) {
        this.initAutoSelect();
      } else if (this.allowDebug()) {
        console.warn('[TrueFit Widget] Cannot refresh - TrueFit API not available');
      }
    }
  }

  customElements.define('truefit-widget', truefitWidget);
}

